/**
 * Pure helpers for the trade/purchase chat (TradeMessages.jsx), kept free of
 * Firebase imports so they can be unit-tested.
 */
const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : null);

export function formatTime(ts) {
  const d = toDate(ts);
  return d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Sending…';
}

export function dayLabel(date, now = new Date()) {
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(date)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString([], opts);
}

/**
 * Messages (oldest first) -> render items: a day separator whenever the
 * calendar day changes, and each message flagged `groupedWithPrev` when the
 * same sender wrote again within 5 minutes (tighter spacing, no corner tail).
 * A message still waiting on its server timestamp counts as today.
 */
export function buildTimeline(messages, myUid, now = new Date()) {
  const items = [];
  let lastDayKey = null;
  let prev = null;
  for (const msg of messages || []) {
    const date = toDate(msg.createdAt) || now;
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    if (dayKey !== lastDayKey) {
      items.push({ type: 'day', key: `day_${dayKey}`, label: dayLabel(date, now) });
      lastDayKey = dayKey;
      prev = null;
    }
    const prevDate = prev ? (toDate(prev.createdAt) || now) : null;
    const groupedWithPrev = !!prev && prev.senderId === msg.senderId && (date - prevDate) < 5 * 60 * 1000;
    items.push({ type: 'msg', msg, mine: msg.senderId === myUid, groupedWithPrev });
    prev = msg;
  }
  return items;
}

function pedalCard(p) {
  if (!p || typeof p !== 'object') return null;
  const name = [p.make, p.model].filter(Boolean).join(' ').trim();
  if (!name) return null;
  const imageUrl = (p.images && p.images.top) || p.imageUrl || (p.placeholderImage && p.placeholderImage.url) || null;
  return { id: p.id || null, name, imageUrl };
}

/**
 * Which pedals each side of a TRADE is sending, from the reader's point of
 * view, for the context card at the top of the chat. The initiator
 * (fromUserId) sends initiatorPedals; the recipient sends responderPedal.
 * Returns null when the trade carries no snapshots (old trades, purchases).
 */
export function tradeSides(trade, myUid) {
  const snaps = trade && trade.pedalSnapshots;
  if (!snaps) return null;
  const initiator = (Array.isArray(snaps.initiatorPedals) && snaps.initiatorPedals.length
    ? snaps.initiatorPedals
    : [snaps.initiatorPedal]).map(pedalCard).filter(Boolean);
  const responder = [snaps.responderPedal].map(pedalCard).filter(Boolean);
  if (!initiator.length && !responder.length) return null;
  const iAmInitiator = trade.fromUserId === myUid;
  return {
    sending: iAmInitiator ? initiator : responder,
    getting: iAmInitiator ? responder : initiator,
  };
}
